import React, { useCallback, useMemo, useRef, useState } from 'react';
import { ScreenHeader } from '@/components/layout/ScreenHeader';
import { MessageView } from '@/components/ui/MessageView';
import { ErrorInListView } from '@/components/ui/ErrorInListView';
import { VStack } from '@/components/gluestack/vstack';
import { Box } from '@/components/gluestack/box';
import { Add01Icon, Calendar03Icon } from '@hugeicons-pro/core-stroke-rounded';
import { ActivityIndicator, RefreshControl, useWindowDimensions } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { InfiniteData, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { EventsService } from '~/services/domain/EventsService';
import {
  PageParam,
  getNextPageSkipOrCursor,
  buildPaginationParams,
} from '@/hooks/usePaginationUtils';
import { FlashList } from '@shopify/flash-list';
import { EventCard, EventCardSkeleton } from '@/components/domain/events/EventCard';
import { HeaderRefreshButton } from '@/components/ui/HeaderRefreshButton';
import { EventsView } from '@/models/Event';
import { useIsLargeScreen } from '@/hooks/useIsLargeScreen';
import { HStack } from '~/components/gluestack/hstack';
import {
  useInfiniteListUtils,
  createSkeletonItems,
  clearStoredScrollPosition,
} from '@/hooks/useInfiniteListUtils';
import { useFlashListOptimizer } from '@/hooks/useFlashListOptimizer';
import { useIsFocused, useScrollToTop } from 'expo-router/react-navigation';
import { useTranslation } from 'react-i18next';
import { ActionBar } from '@/components/layout/ActionBar';
import { getEventListItems } from '@/components/domain/events/eventListItems';
import { HeaderMenuSelector, HeaderMenuSelectorTitle } from '@/components/ui/HeaderMenuSelector';
import { useRouter } from 'expo-router';
import { Fab } from '~/components/gluestack/fab';
import { HugeIconsWrapper } from '~/components/ui/HugeIconsWrapper';
import { ContextSheet } from '@/components/ui/ContextSheet';
import { AddEditEvent } from '@/components/domain/events/AddEditEvent';
import { CountBadge } from '@/components/ui/CountAndMyTypeBadge';

import { EventListType } from '~/constants/lists';
type EventScreenListType = EventListType | 'member';

interface EventsScreenProps {
  initialListType?: EventListType;
  memberId?: number;
  showHeader?: boolean;
}

export function EventsScreen({
  initialListType = 'upcoming',
  memberId,
  showHeader = true,
}: EventsScreenProps) {
  const { t } = useTranslation();
  const isLargeScreen = useIsLargeScreen();
  const isScreenActive = useIsFocused();
  const flashListRef = useRef<React.ComponentRef<typeof FlashList<any>>>(null);
  const { width } = useWindowDimensions();
  const { onEndReachedThreshold, pageSize } = useFlashListOptimizer({
    estimatedRowsOnScreen: { mobile: 4, desktop: 3 },
    maxColsOnScreen: { mobile: 2, desktop: 6 },
  });
  const router = useRouter();
  const queryClient = useQueryClient();
  const { bottom: safeAreaBottom } = useSafeAreaInsets();

  const isMemberMode = !!memberId; // used to show a member's events (on profile screen)
  const listType: EventScreenListType = isMemberMode ? 'member' : initialListType;
  const [isCreateEventSheetOpen, setIsCreateEventSheetOpen] = useState(false);

  const queryKey = listType === 'member' ? ['events', listType, memberId] : ['events', listType];

  const { handleRefresh, isUserRefreshing, showHeaderRefetchIndicator, onScroll } =
    useInfiniteListUtils({
      queryKey,
      flashListRef,
      pageKey: `/events/${listType}`,
    });

  useScrollToTop(flashListRef);

  const getServiceMethod = (type: EventScreenListType) => {
    switch (type) {
      case 'upcoming':
        return EventsService.getUpcomingEvents;
      case 'nearby':
        return EventsService.getNearbyEvents;
      case 'network':
        return EventsService.getNetworkEvents;
      case 'past':
        return EventsService.getPastEvents;
      case 'member':
        return (params?: any) => EventsService.getMemberEvents(memberId!, params);
      default:
        throw new Error(`Unknown event list type: ${type}`);
    }
  };

  const {
    data,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isRefetching,
    isPending,
    refetch,
  } = useInfiniteQuery({
    queryKey,
    queryFn: async ({ pageParam }) => {
      const serviceMethod = getServiceMethod(listType);
      return await serviceMethod(buildPaginationParams(pageParam, pageSize));
    },
    enabled: listType !== 'member' || !!memberId,
    initialPageParam: undefined as PageParam,
    getNextPageParam: getNextPageSkipOrCursor,
    select: useCallback((data: InfiniteData<EventsView, PageParam>) => {
      const items = data.pages.flatMap((page) => page.EventsItems);
      return { ...data, items };
    }, []),
  });

  const canCreateEvents = !isMemberMode && data?.pages?.[0]?.CanCreateEvents === true;
  const itemCount = data?.pages?.[0]?.ItemCount ?? 0;

  const handleEventCreated = (result?: { Id: number; Code: string }) => {
    if (result?.Code) {
      router.push(`/events/${result.Code}` as any);
    }
  };

  const getNumColumns = () => {
    if (!isLargeScreen) return 2;
    if (width >= 1536) return 6;
    if (width >= 1024) return 4;
    if (width >= 768) return 3;
    return 2;
  };
  const numColumns = getNumColumns();

  const skeletonItems = useMemo(() => createSkeletonItems(numColumns * 2), [numColumns]);

  const eventListItems = useMemo(() => getEventListItems(t), [t]);

  const navigateToList = useCallback(
    (newListType: EventListType) => {
      if (newListType === listType) return;

      queryClient.cancelQueries({ queryKey: ['events', listType] });

      // Sideways navigation between sibling lists should not restore the destination's
      // previous scroll position; only stack-pop returns (e.g. back from an event) should.
      clearStoredScrollPosition(`/events/${newListType}`);

      router.replace(`/events/${newListType}` as any);
    },
    [listType, queryClient, router]
  );

  const getListTitle = () => {
    switch (listType) {
      case 'upcoming':
        return t('events.upcoming');
      case 'nearby':
        return t('events.nearby');
      case 'network':
        return t('events.network');
      case 'past':
        return t('events.past');
      case 'member':
        return t('navigation.events');
      default:
        return t('navigation.events');
    }
  };

  const ItemWrapper = useMemo(
    () =>
      ({ children }: { children: React.ReactNode }) => (
        <Box className="mt-3 flex-1 pr-3 md:mb-2 md:mt-2 md:pr-4">{children}</Box>
      ),
    []
  );

  const flashListContainerStyle = {
    paddingLeft: isLargeScreen ? 16 : 11,
    paddingBottom: isLargeScreen ? safeAreaBottom : 0,
  };

  const emptyMessage = listType === 'member' ? t('events.noEventsForMember') : t('events.empty');

  return (
    <VStack className="flex-1">
      {showHeader && (
        <ScreenHeader
          options={{
            hideBackButton: true,
            title: isLargeScreen ? getListTitle() : t('navigation.events'),
            ...(isLargeScreen
              ? {
                  ...(!isMemberMode
                    ? {
                        headerTitle: () => (
                          <HeaderMenuSelectorTitle
                            value={listType as EventListType}
                            items={eventListItems}
                            title={getListTitle()}
                          />
                        ),
                      }
                    : {}),
                  headerLeft: () => (
                    <HStack className="items-center gap-3">
                      <CountBadge count={itemCount} />
                      {showHeaderRefetchIndicator && <ActivityIndicator color="grey" />}
                    </HStack>
                  ),
                  headerRight: () => (
                    <HeaderRefreshButton
                      isLargeScreen={isLargeScreen}
                      isRefreshing={isUserRefreshing}
                      onPress={handleRefresh}
                    />
                  ),
                }
              : !isMemberMode
                ? {
                    headerBottom: () => (
                      <ActionBar
                        leftSection={
                          <HStack className="items-center gap-3">
                            <HeaderMenuSelector
                              value={listType as EventListType}
                              onChange={navigateToList}
                              items={eventListItems}
                              triggerLabel={t(`events.${listType}_short`)}
                            />
                            <CountBadge count={itemCount} />
                          </HStack>
                        }
                        rightSection={
                          <HeaderRefreshButton
                            isLargeScreen={isLargeScreen}
                            isRefreshing={isUserRefreshing}
                            onPress={handleRefresh}
                          />
                        }
                      />
                    ),
                  }
                : {}),
          }}
        />
      )}

      {error ? (
        <ErrorInListView error={error} onRetry={() => refetch()} isRetrying={isRefetching} />
      ) : (
        <FlashList
          ref={flashListRef}
          data={isPending ? skeletonItems : data?.items}
          renderItem={({ item }) => (
            <ItemWrapper>
              {isPending ? (
                <EventCardSkeleton />
              ) : (
                <EventCard event={item} key={`event-${item.Id}`} />
              )}
            </ItemWrapper>
          )}
          keyExtractor={(item, index) => (isPending ? `s-${index}` : `event-${item.Id}`)}
          numColumns={numColumns}
          onEndReached={() => {
            if (!isPending && hasNextPage && isScreenActive && !isFetchingNextPage) {
              fetchNextPage();
            }
          }}
          onEndReachedThreshold={onEndReachedThreshold}
          ListEmptyComponent={
            <MessageView type="info" text={emptyMessage} icon={Calendar03Icon} listEmpty />
          }
          ListFooterComponent={() => (
            <Box style={{ width: '100%' }}>
              {isFetchingNextPage && (
                <Box className="m-4">
                  <ActivityIndicator color="grey" />
                </Box>
              )}
            </Box>
          )}
          refreshControl={
            <RefreshControl refreshing={isUserRefreshing} onRefresh={handleRefresh} />
          }
          contentContainerStyle={flashListContainerStyle}
          onScroll={onScroll}
        />
      )}

      {canCreateEvents && (
        <Fab
          size="lg"
          className="bg-primary-500 hover:bg-primary-700 active:bg-primary-800"
          onPress={() => setIsCreateEventSheetOpen(true)}>
          <HugeIconsWrapper icon={Add01Icon} className="text-white" />
        </Fab>
      )}

      <ContextSheet
        isOpen={isCreateEventSheetOpen}
        onClose={() => setIsCreateEventSheetOpen(false)}
        contentComponent={AddEditEvent}
        title={t('events.createEvent')}
        payload={{
          onEventSaved: handleEventCreated,
        }}
      />
    </VStack>
  );
}
